import { apiRequest } from '../client'

export type CountSessionStatus = 'OPEN' | 'CLOSED' | 'EXPIRED' | 'ABORTED'
export type CountItemResolution =
  | 'PENDING'
  | 'MATCHED'
  | 'CONFIRMED'
  | 'CONFIRMED_ZERO'
  | 'SKIPPED'
export type CountVarianceReason =
  | 'BREAKAGE'
  | 'EXPIRY'
  | 'THEFT'
  | 'MISCOUNT'
  | 'UNKNOWN'
export type CountItemAction =
  | 'recount'
  | 'confirm'
  | 'confirm_zero'
  | 'skip'
  /**
   * Takes a decision back while the session is open: any applied correction is reversed by a
   * compensating movement and the article returns to PENDING
   * (CHG-2026-09-inventory-count-review-ux). Rejected with 400 `not_resolved` on an article
   * that carries no decision yet.
   */
  | 'reopen'

export interface CountSessionView {
  id: string
  restaurantId: number
  status: CountSessionStatus
  startedBy: string
  startedAt: string
  lastActivityAt: string
  finishedBy: string | null
  finishedAt: string | null
}

export interface AffectedOrder {
  orderId: string
  pickupCode: string | null
  pickupAt: string | null
}

export interface CountItemView {
  sku: string
  name: string
  countedQty: number | null
  expectedAvailable: number
  expectedReserved: number
  delta: number | null
  recounted: boolean
  resolution: CountItemResolution
  reason: CountVarianceReason | null
  resolvedBy: string | null
  resolvedAt: string | null
  affectedOrders: Array<AffectedOrder>
}

export interface CountUnknownScanView {
  code: string
  qty: number
  capturedAt: string
}

export interface CountSessionDetail {
  session: CountSessionView
  items: Array<CountItemView>
  unknownScans: Array<CountUnknownScanView>
  /**
   * Read-only must-count size ("n / m" progress), computed by the backend without materializing
   * any review rows. Drives the capture screen's progress line instead of calling review().
   */
  expectedCount: number
  /**
   * When the inactivity sweep would end this session: last activity plus the server's configured
   * window. Lets the flow show the deadline without knowing that configuration.
   */
  expiresAt: string | null
}

export interface CountReviewResult {
  session: CountSessionView
  matches: Array<CountItemView>
  variances: Array<CountItemView>
  notCounted: Array<CountItemView>
  unknownScans: Array<CountUnknownScanView>
}

export interface CountProductRef {
  sku: string
  name: string
  imageUrl: string | null
}

/**
 * One article of the counted location's assortment. `mustCount` is the must-count predicate
 * (a stock record with on-hand > 0); `expectedQty` is ABSENT — not null — when the location is
 * configured to count blind, so a blind count cannot be read off the network response.
 */
export interface CountAssortmentItem {
  sku: string
  name: string
  barcode: string | null
  /** Category name for grouping the open list; absent for uncategorized products. */
  category?: string
  mustCount: boolean
  expectedQty?: number
  /** CHG-2026-09-stock-usage-and-price-twins: the base unit as shown ("Stk" by default). */
  unit?: string
  /** "Teilmengen zählen": whether this article may be counted in partial amounts. */
  fractionalCounting?: boolean
}

export interface CountAssortment {
  items: Array<CountAssortmentItem>
  /** Mirrors the location's setting, so the quantity sheet can be labelled without guessing. */
  showExpected: boolean
  generatedAt: string
}

/**
 * Per-session rollup returned on every history row so the list can show real numbers without one
 * detail fetch per session: `itemCount` is the number of item rows recorded (captured + the
 * not-counted rows materialized at review/close), `confirmedVarianceCount` counts items that
 * actually carry an applied correction (CONFIRMED or CONFIRMED_ZERO), and `correctionUp`/
 * `correctionDown` are the positive/negative sums of those confirmed deltas.
 */
export interface CountSessionSummary {
  itemCount: number
  confirmedVarianceCount: number
  correctionUp: number
  correctionDown: number
}

export interface CountSessionHistoryRow {
  session: CountSessionView
  summary: CountSessionSummary
}

const base = (restaurantId: number) => `/api/v0/admin/inventory/${restaurantId}`

export const inventoryCountSessionsApi = {
  open: (restaurantId: number) =>
    apiRequest<CountSessionView>(`${base(restaurantId)}/count-sessions`, {
      method: 'POST',
    }),

  current: (restaurantId: number) =>
    apiRequest<CountSessionDetail>(
      `${base(restaurantId)}/count-sessions/current`,
    ),

  history: (restaurantId: number, offset = 0, limit = 50) =>
    apiRequest<{ items: Array<CountSessionHistoryRow>; total: number }>(
      `${base(restaurantId)}/count-sessions?offset=${offset}&limit=${limit}`,
    ),

  detail: (restaurantId: number, sessionId: string) =>
    apiRequest<CountSessionDetail>(
      `${base(restaurantId)}/count-sessions/${sessionId}`,
    ),

  resolveCode: (restaurantId: number, code: string) =>
    apiRequest<CountProductRef>(
      `${base(restaurantId)}/resolve?code=${encodeURIComponent(code)}`,
    ),

  /**
   * The location's whole assortment, fetched once per session so search, the outstanding list and
   * EAN resolution work on the device. Read-only: it materializes nothing and does not touch the
   * session.
   */
  assortment: (restaurantId: number) =>
    apiRequest<CountAssortment>(`${base(restaurantId)}/count-assortment`),

  /**
   * `mode: 'add'` (default) is the scan/search path and accumulates onto the SKU's running total;
   * `mode: 'set'` overwrites it and is the capture screen correcting a mistyped quantity (0 allowed).
   *
   * `captureId` is the idempotency key: reuse the same one on every resend of a capture, and a
   * fresh one for every new capture. A resend under a known id changes nothing and returns the
   * item as it stands, so a retry after a timeout cannot count twice.
   */
  capture: (
    restaurantId: number,
    sessionId: string,
    body: {
      sku: string
      qty: number
      mode?: 'add' | 'set'
      captureId?: string
    },
  ) =>
    apiRequest<CountItemView>(
      `${base(restaurantId)}/count-sessions/${sessionId}/captures`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /** Drops a captured row (wrong article scanned). Only allowed while the item is PENDING. */
  deleteCapture: (restaurantId: number, sessionId: string, sku: string) =>
    apiRequest<void>(
      `${base(restaurantId)}/count-sessions/${sessionId}/items/${encodeURIComponent(sku)}`,
      { method: 'DELETE' },
    ),

  unknownScan: (
    restaurantId: number,
    sessionId: string,
    body: { code: string; qty: number },
  ) =>
    apiRequest<{ ok: boolean }>(
      `${base(restaurantId)}/count-sessions/${sessionId}/unknown-scans`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  review: (restaurantId: number, sessionId: string) =>
    apiRequest<CountReviewResult>(
      `${base(restaurantId)}/count-sessions/${sessionId}/review`,
      { method: 'POST' },
    ),

  resolveItem: (
    restaurantId: number,
    sessionId: string,
    sku: string,
    body: {
      action: CountItemAction
      reason?: CountVarianceReason
      recountQty?: number
      /**
       * One-tap surplus confirm: satisfies the "recount before confirm" gate atomically within
       * the same call instead of requiring a separate `recount` resolve first.
       */
      recountConfirmed?: boolean
    },
  ) =>
    apiRequest<CountItemView>(
      `${base(restaurantId)}/count-sessions/${sessionId}/items/${encodeURIComponent(sku)}/resolve`,
      { method: 'POST', body: JSON.stringify(body) },
    ),

  /**
   * Ignores every remaining not-counted article in one request instead of one resolve per article,
   * and answers how many rows it settled. Ignore-only by construction: the endpoint has no
   * confirm-zero path, which stays per article.
   */
  skipRemaining: (restaurantId: number, sessionId: string) =>
    apiRequest<{ skipped: number }>(
      `${base(restaurantId)}/count-sessions/${sessionId}/items/skip-remaining`,
      { method: 'POST' },
    ),

  close: (restaurantId: number, sessionId: string) =>
    apiRequest<CountSessionView>(
      `${base(restaurantId)}/count-sessions/${sessionId}/close`,
      { method: 'POST' },
    ),

  abort: (restaurantId: number, sessionId: string) =>
    apiRequest<CountSessionView>(
      `${base(restaurantId)}/count-sessions/${sessionId}/abort`,
      { method: 'POST' },
    ),
}
